/** @jsx h */
import { h, render } from 'preact';
import joinUrl from '@ogury/url-join';
import { unmarshaller as formatUnmarshaller } from './config';
import { getTrackingUrlMacros } from './lib/utils';
import VideoContainer from './components/video-container';
import getActions, { onError, setDeliveryOptions } from './lib/actions';
import * as vpaid from '../../common/js/vpaid/vpaid';
import {
  getTrackingFramework,
  getVpaidMediaFiles,
  isMediaFileVpaid,
  shouldLoadVpaidVideoFile,
  TRACKING_FRAMEWORK_VPAID,
  TrackingFramework
} from '../../common/js/tracking-helper';
import {
  sendCreativeHeaderAdAboveHeader,
  sendCreativeHeaderAdBelowHeader,
  sendCreativeHeaderMinimizedDefault,
  sendCreativeLoaded,
  sendCreativeVideoErrorAutoPlay,
  sendCreativeVpaidSelected,
  sendMonitoringVideoChosenEvent,
  sendMonitoringVideoChosenNewVideoSelectionEvent,
  setHasVpaid
} from '../../common/js/TrackAnalyticsHelper';
import { templateMacrosEncoded } from '../../common/js/url-utils';
import { deviceSupportAutoplayDetect, loadVideo } from './lib/video-loader';
import { mutateConfigForVpaid } from '../../common/js/vpaid/vpaid-config';
import { getOgyAdUnitParam, isHeaderAdUnit } from '../../common/js/adUnit';
import { RootStore } from '../../common/js/stores/root.store';
import VASTError from './lib/vast/vast-error';
import ajaxPromises from '../../common/js/AjaxPromises';

import { once } from '../../common/js/helpers';
import { EndScreenOrchestrator } from './lib/end-screen/orchestrator';
import { MRCViewabilityTracker } from './lib/mrc-viewability-tracker';
import { DebugLog } from '../../common/js/debug-log';

import { preloadBrowser, prepareFormat } from './lib/init-format';
import { LoadingStrategyFactory } from './lib/strategies/loading-strategy-factory';
import { VideoSizesHandler } from '../../common/js/stores/handlers/video-sizes.handler';
import { EdgeToEdgeDectectionIssueHandler } from '../../common/js/stores/handlers/edge-to-edge-detection-issue-handler';
import { RenderTimeHandler } from '../../common/js/stores/handlers/render-time.handler';
import { NoUserHandler } from './lib/handlers/no-user.handler';
import { OmidEventsHandler } from '../../common/js/stores/handlers/omid-events.handler';
import { AdContainerDebugTrackHandler } from './lib/handlers/ad-container-debug-track.handler';
import { HeaderAdResizeHandler } from './lib/handlers/header-ad-resize.handler';
import { SentryTagsHandler } from '../../common/js/stores/handlers/sentry-tags.handler';
import { MediaFile } from './lib/vast/media-file';
import VASTClient from './lib/vast/vast-client';
import { FormatContext } from './lib/strategies/loading-strategy';
import { getMwebSupplyEventsHandlers } from '../../common/js/stores/handlers/mweb-supply-events-handlers';
import { VastImpressionDebugHandler } from './lib/handlers/vast-impression-debug.handler';

interface TrackingUrlMacroConfig {
  campaignId?: string;
  richadvertId?: string;
  'aaid-optout': number;
  apikey?: string;
  aaid?: string;
  advertiserId?: string;
  advertiserName?: string;
  impressionId?: string;
  sdkVersion?: string;
  deviceOs?: string;
  appBundle?: string;
  page?: string;
  domain?: string;
  ioId?: string;
  userInstanceToken?: string;
  gdpr?: boolean;
  tcfv2?: string;
  creativeId?: string;
}

let autoPlayDetectionPromise: Promise<void>;

const notifyAdLoaded = once(function() {
  DebugLog.log('Video SDK3 notifyAdLoaded called');
  autoPlayDetectionPromise
    .then(() => {
      DebugLog.log(
        'Video SDK3 autoplay detection succeeded, calling ogyOnAdLoaded'
      );
      RootStore.getInstance().adContainerStore.triggerOnAdLoaded();
      sendCreativeLoaded();
    })
    .catch((error: Error & { additionalTrace?: any }) => {
      DebugLog.log('Video SDK3 autoplay detection failed', {
        error: error.message
      });
      if (error instanceof VASTError) {
        const { vast } = RootStore.getInstance().videoStore;
        if (vast) {
          error.additionalTrace = {
            fetchedRawVasts: vast.getFetchedRawVasts(),
            sendingCreativeErrorAutoPlay: true
          };
        }

        sendCreativeVideoErrorAutoPlay();
      }
      onError(null, error);
    });
});

function renderComponent(
  mediaFiles: MediaFile[],
  macros: Record<string, any>,
  trackingUrlMacroConfig: TrackingUrlMacroConfig,
  options: FormatOptions,
  config: FormatConfig
): void {
  const videoStore = RootStore.getInstance().videoStore;
  const vast = videoStore.vast!;

  if (vast.hasViewableImpression()) {
    new MRCViewabilityTracker().startMonitoring();
  }

  EndScreenOrchestrator.instance.init({
    config,
    context: options.context,
    vastClient: vast
  });

  videoStore.setMediaFile(mediaFiles[0]);

  // TODO: put the following piece of code in a new file for vpaid => https://jira.ogury.io/browse/SPY-14898
  const trackingFramework: TrackingFramework = getTrackingFramework(
    mediaFiles,
    config
  );

  const loadVpaidFile: boolean = shouldLoadVpaidVideoFile(mediaFiles, config);
  videoStore.isVpaid = loadVpaidFile;

  if (loadVpaidFile) {
    const vpaidMediaFiles = getVpaidMediaFiles(mediaFiles);
    if (vpaidMediaFiles[0]) {
      // Need to replace url encoded macros for moat
      const vpaidUrl = vpaidMediaFiles[0].url.trim();
      vpaidMediaFiles[0].url = templateMacrosEncoded(vpaidUrl || '', macros);
      // set media file to vpaid for chosen event
      videoStore.setMediaFile(vpaidMediaFiles[0] as any);
    }
  }

  setHasVpaid(vast.hasVpaidMediaFile());

  if (loadVpaidFile) {
    sendCreativeVpaidSelected();

    [
      'AdLoaded',
      'AdStarted',
      'AdStopped',
      'AdVideoStart',
      'AdPaused',
      'AdPlaying',
      'AdError'
    ].forEach(eventName => {
      vpaid.addEventListener(eventName, () => {
        vpaid.setVpaidState(eventName);
      });
    });

    vpaid.addEventListener('AdError', onError as any);
    vpaid.addEventListener('AdLoaded', () => notifyAdLoaded());

    vpaid.addEventListener('AdLoadedThreshold', () => notifyAdLoaded());

    const vpaidOptions: vpaid.VpaidOptions = {
      adUnitType: options.context.adUnitType,
      isTrackingEnabled: trackingFramework === TRACKING_FRAMEWORK_VPAID
    };
    vpaid.init(vast, mediaFiles, vpaidOptions, macros);

    // Override configuration
    config = mutateConfigForVpaid(config);
  }

  const isVpaidMediaLoaded: boolean =
    mediaFiles && mediaFiles[0] && isMediaFileVpaid(mediaFiles[0]);

  if (isVpaidMediaLoaded) {
    sendCreativeVpaidSelected();
  }
  // @TODO: end of vpaid logic

  if (config.video && config.video.newVideoSelection) {
    sendMonitoringVideoChosenNewVideoSelectionEvent();
  } else {
    sendMonitoringVideoChosenEvent();
  }

  const actions = getActions(
    {
      ...config,
      trackingUrlMacroConfig
    },
    options
  );

  // TODO: put in a new strategy HeaderAdStrategy to apply this piece of code before rendering video => https://jira.ogury.io/browse/SPY-14898
  if (isHeaderAdUnit(options.context.adUnitType)) {
    if (getOgyAdUnitParam('minimized_by_default')) {
      sendCreativeHeaderMinimizedDefault();
    }
    if (getOgyAdUnitParam('header_ad_dom_position') === 'BELOW_HEADER') {
      sendCreativeHeaderAdBelowHeader();
    }
    if (getOgyAdUnitParam('header_ad_dom_position') === 'ABOVE_HEADER') {
      sendCreativeHeaderAdAboveHeader();
    }
  }

  // Start video rendering
  render(
    <VideoContainer
      containerId="format"
      config={{
        ...config,
        mediaFiles
      }}
      actions={actions as any}
      options={options}
      trackingFramework={trackingFramework}
    />,
    options.container
  );

  DebugLog.log('Video SDK3 renderComponent completed, calling notifyAdLoaded', {
    loadVpaidFile
  });
  !loadVpaidFile && notifyAdLoaded();
}

window.initFormat = (options: FormatOptions): void => {
  DebugLog.log('Video SDK3 initFormat', options);

  const formatName = 'Video';
  prepareFormat(
    {
      formatName,
      formatUnmarshaller,
      options
    },
    (formatContext: FormatContext) => {
      RootStore.getInstance()
        .initStore(formatContext.config, formatContext.adContext, options)
        .initHandlers(
          VideoSizesHandler,
          EdgeToEdgeDectectionIssueHandler,
          RenderTimeHandler,
          NoUserHandler,
          OmidEventsHandler,
          AdContainerDebugTrackHandler,
          HeaderAdResizeHandler,
          SentryTagsHandler,
          ...getMwebSupplyEventsHandlers()
        );

      new VastImpressionDebugHandler(RootStore.getInstance(), options);

      options.context = formatContext.adContext;

      preloadBrowser((formatContext.config as any).browser);

      // eslint-disable-next-line
      window.__webpack_public_path__ = joinUrl(
        (formatContext.config as any).formatBaseUrl,
        '/formats/video-sdk3/'
      );

      // TODO: handle prefetching of video in a new function => https://jira.ogury.io/browse/SPY-14898
      // TODO: trackingUrlMacroConfig seems to be redondant with getTrackingUrlMacros method, both should be merged => https://jira.ogury.io/browse/SPY-14898
      const trackingUrlMacroConfig: TrackingUrlMacroConfig = {
        campaignId: formatContext.adContext.campaignId,
        richadvertId: formatContext.adContext.richadvertId,
        'aaid-optout': 0,
        apikey: formatContext.adContext.apiKey,
        aaid: formatContext.adContext.shareDeviceId
          ? formatContext.adContext.userId
          : '',
        advertiserId: formatContext.adContext.advertiserId,
        advertiserName: formatContext.adContext.advertiserName,
        impressionId: formatContext.adContext.impressionId,
        sdkVersion: formatContext.adContext.sdkVersion,
        deviceOs: formatContext.adContext.deviceOs,
        appBundle: formatContext.adContext.appBundle,
        page: formatContext.adContext.page,
        domain: formatContext.adContext.domain,
        ioId: formatContext.adContext.ioId,
        userInstanceToken: formatContext.adContext.userInstanceToken,
        gdpr: formatContext.adContext.gdpr,
        tcfv2: formatContext.adContext.tcfv2,
        creativeId: formatContext.adContext.creativeId
      };

      const macros = getTrackingUrlMacros(trackingUrlMacroConfig);

      autoPlayDetectionPromise = deviceSupportAutoplayDetect(
        (formatContext.config as any).alternativeAutoplayDetect,
        (formatContext.config as any).alternativeAutoplayLog
      );

      // TODO: to remove, it seems redondant with what has been done in the getActions method => https://jira.ogury.io/browse/SPY-14898
      setDeliveryOptions(options);

      // Video prefetching
      try {
        const result = loadVideo(
          formatContext.config,
          macros,
          formatContext.adContext
        );

        ajaxPromises.waitPromiseOrExecute(
          result,
          ({
            vastClient,
            mediaFiles
          }: {
            vastClient: VASTClient;
            mediaFiles: MediaFile[];
          }) => {
            const updatedContext = LoadingStrategyFactory.getInstance().afterVideoLoaded(
              {
                ctx: formatContext,
                result: { vastClient, mediaFiles }
              }
            ).ctx;
            RootStore.getInstance().videoStore.vast = vastClient;
            renderComponent(
              mediaFiles,
              macros,
              trackingUrlMacroConfig,
              options,
              updatedContext.config
            );
          },
          (error: Error) => {
            onError(null, error, true, formatContext.adContext);
          }
        );
      } catch (error) {
        onError(null, error as Error, true, formatContext.adContext);
      }
    }
  );
};
